import { DAY, dateOf, daysBetween, toInstant, addDays, type LocalDate, type LocalDateTime } from './time';
import { assertNonNegative, percentCeilTo, sum, type Rupees } from './money';
import { fail } from './errors';
import type { BookingRecord, Item, Package, PackageUnit, QuoteTerms, Settings } from './types';

/**
 * Chargeable units for one item.
 *  - 24h (stays): ceil(elapsed / 24 h), minimum 1. Exact: one minute over 24 h is a second unit.
 *    (The prototype subtracted 0.01 day, silently allowing ~14 min of overstay; that fudge is removed.)
 *  - day (events): IST calendar days touched by [start, end). An event ending exactly at 00:00
 *    does not touch the next day.
 */
export function units(unit: PackageUnit, start: LocalDateTime, end: LocalDateTime): number {
  const a = toInstant(start), b = toInstant(end);
  if (!(b > a)) fail('invalid', 'End must be after start');
  if (unit === '24h') return Math.max(1, Math.ceil((b - a) / DAY));
  const lastTouched = dateOf(b - 1);
  return daysBetween(dateOf(a), lastTouched) + 1;
}

export function itemUnits(item: Pick<Item, 'kind' | 'start' | 'end'>): number {
  return units(item.kind === 'stay' ? '24h' : 'day', item.start, item.end);
}

export function unitsLabel(item: Pick<Item, 'kind' | 'start' | 'end'>): string {
  const n = itemUnits(item);
  return item.kind === 'stay' ? `${n} × 24 h` : n === 1 ? '1 day' : `${n} days`;
}

/** Standard price for a package over a period, from the given (current) settings. */
export function standardPrice(pkg: Package, start: LocalDateTime, end: LocalDateTime): Rupees {
  return pkg.custom ? 0 : pkg.base * units(pkg.unit, start, end);
}

/** Build a quoted item, snapshotting the package's current name, price and deposit. */
export function quoteItem(pkg: Package, start: LocalDateTime, end: LocalDateTime, agreed?: Rupees): Item {
  const std = standardPrice(pkg, start, end);
  if (agreed !== undefined) assertNonNegative(agreed, 'agreed price');
  const it: Item = { pkg: pkg.id, pkgName: pkg.name, kind: pkg.kind, start, end, std, agreed: agreed ?? std, deposit: pkg.deposit };
  if (pkg.custom) it.custom = true;
  return it;
}

/** A Customize item whose price has not been agreed yet. Holds and confirmations wait for it. */
export function pricePending(r: Pick<BookingRecord, 'items'>): boolean {
  return r.items.some(i => i.custom && i.agreed <= 0);
}

export function requirePrice(r: Pick<BookingRecord, 'items'>, action: string) {
  const it = r.items.find(i => i.custom && i.agreed <= 0);
  if (it) fail('invalid', `Agree the price for ${it.pkgName} before you ${action}`);
}

/** ₹2,000 per event plus ₹1,000 per extra event day. Stays: none. */
export function cleaningFor(items: readonly Pick<Item, 'kind' | 'start' | 'end'>[], s: Pick<Settings, 'cleaning' | 'cleaningExtra'>): Rupees {
  const days = sum(items.filter(i => i.kind === 'event').map(itemUnits));
  return days ? s.cleaning + s.cleaningExtra * (days - 1) : 0;
}

/** Combined bookings take the higher item deposit. */
export function depositFor(items: readonly Pick<Item, 'deposit'>[]): Rupees {
  return items.length ? Math.max(...items.map(i => i.deposit)) : 0;
}

/** Advance to confirm: pct of total rounded UP to the next ₹100, or a fixed amount (never above total). */
export function advanceFor(total: Rupees, t: Pick<QuoteTerms, 'advanceMode' | 'advancePct' | 'advance'>): Rupees {
  if (total <= 0) return 0;
  return t.advanceMode === 'percent' ? percentCeilTo(total, t.advancePct, 100) : Math.min(t.advance, total);
}

export function termsFrom(s: Settings): QuoteTerms {
  return {
    advanceMode: s.advanceMode, advancePct: s.advancePct, advance: s.advance,
    balanceDueDays: s.balanceDueDays, cancel: { ...s.cancel }, refundWindow: s.refundWindow, bufferHours: s.bufferHours,
  };
}

export function span(r: Pick<BookingRecord, 'items'>): { start: LocalDateTime; end: LocalDateTime } {
  if (!r.items.length) fail('invalid', 'Record has no items');
  let s = r.items[0]!.start, e = r.items[0]!.end;
  for (const i of r.items) {
    if (toInstant(i.start) < toInstant(s)) s = i.start;
    if (toInstant(i.end) > toInstant(e)) e = i.end;
  }
  return { start: s, end: e };
}

/** Balance due date: start date − balanceDueDays (from the record's quote terms). */
export function balanceDue(r: Pick<BookingRecord, 'items' | 'terms'>): LocalDate {
  return addDays(dateOf(span(r).start), -r.terms.balanceDueDays);
}

const NO_BALANCE = new Set(['enquiry', 'lost', 'expired', 'cancelled']);

export interface Calc {
  /** custom: agreed prices of Customize items, which have no standard to compare against. */
  std: Rupees; agreed: Rupees; custom: Rupees; pending: boolean; extra: Rupees; disc: Rupees; clean: Rupees; damage: Rupees;
  total: Rupees; received: Rupees; refunded: Rupees; paid: Rupees; balance: Rupees;
  advanceDue: Rupees;
  payState: 'Unpaid' | 'Part paid' | 'Paid in full';
  dRequired: Rupees; dCollected: Rupees; dDeducted: Rupees; dRefunded: Rupees; dHeld: Rupees; dStatus: string;
}

/**
 * Money figures for a record. `expired` here is passed by the caller (phase) because a hold whose time
 * has passed has no balance even before its stored status is updated.
 */
export function calc(r: BookingRecord, opts: { expired?: boolean } = {}): Calc {
  const std = sum(r.items.map(i => i.std)), agreed = sum(r.items.map(i => i.agreed));
  const custom = sum(r.items.filter(i => i.custom).map(i => i.agreed));
  const extra = r.extra?.amount ?? 0, disc = r.discount?.amount ?? 0, clean = r.cleaning ?? 0, damage = r.damage ?? 0;
  const total = agreed + extra + clean - disc + damage;
  const live = r.payments.filter(p => !p.voided);
  const received = sum(live.filter(p => p.type !== 'refund').map(p => p.amount));
  const refunded = sum(live.filter(p => p.type === 'refund').map(p => p.amount));
  const paid = received - refunded;
  const balance = NO_BALANCE.has(r.status) || opts.expired ? 0 : total - paid;
  const d = r.deposit;
  const dCollected = d.collected?.amount ?? 0, dDeducted = sum(d.deductions.map(x => x.amount)), dRefunded = d.refund?.amount ?? 0;
  const dHeld = d.collected && (d.status === 'held' || d.status === 'inspection') ? dCollected : 0;
  return {
    std, agreed, custom, pending: pricePending(r), extra, disc, clean, damage, total, received, refunded, paid, balance,
    advanceDue: advanceFor(total, r.terms),
    payState: paid <= 0 ? 'Unpaid' : total - paid <= 0 ? 'Paid in full' : 'Part paid',
    dRequired: d.required, dCollected, dDeducted, dRefunded, dHeld, dStatus: d.status,
  };
}

/** Suggested extra-guest charge (owner decides): rate × guests over standard capacity, × units for stays. */
export function extraGuestSuggestion(pkg: Package, guests: number, start: LocalDateTime, end: LocalDateTime, rate: Rupees): { over: number; amount: Rupees } {
  if (pkg.custom) return { over: 0, amount: 0 };
  const over = Math.max(0, guests - pkg.capacity);
  return { over, amount: over * rate * (pkg.unit === '24h' ? units('24h', start, end) : 1) };
}

export type CapacityState = 'ok' | 'over-standard' | 'over-max';
export function capacityState(pkg: Package, guests: number): CapacityState {
  if (guests > (pkg.max ?? pkg.capacity)) return 'over-max';
  if (guests > pkg.capacity) return 'over-standard';
  return 'ok';
}

